<template>
  <div class="page">
    <WhenOrientation value="portrait">
      <div style="margin: 20px; padding: 20px; border-radius: 10px; text-align: center; background: rgba(50, 100, 255, 0.1);">
        Rotate your device into <span style="font-weight: bold;">landscape orientation</span> to view mode schedules.
      </div>
    </WhenOrientation>
    <WhenOrientation value="landscape">
      <div style="display: flex; min-height: 0; flex-grow: 1;">
        <div class="clocks-page--toolbar">
          <div class="picker-title">
            Fill size
          </div>
          <div style="display: flex; gap: 2px; padding-top: 5px; padding-bottom: 5px;">
            <Button v-for="option in fillSizeOptions" severity="secondary" size="small" class="fillSize" 
              :class="fillSizeOptionsClass(option)" 
              @click="selectedFillSize = option.value"
            >
              {{ option.text}}
            </Button>
          </div>
          <div class="picker-title">
            Modes
          </div>
          <ClockPicker
            :bins="bins"
            :options="modeOptions"
            v-model:selectedOption="selectedOption"
          ></ClockPicker>
          <template v-if="anyPurgeEnabled">
            <div class="picker-title">
              Purges
            </div>
            <ClockPicker 
              :bins="bins"
              :options="purgeOptions" 
              v-model:selectedOption="selectedOption"
            ></ClockPicker>
          </template>
          <div v-if="anyDryingEnabled" class="picker-title">
            Drying
          </div>
          <div v-if="anyHeatingUnitClocksEnabled" class="picker-title">
            Heating
          </div>
          <div v-if="anyHumidifiersInTimedOutputMode" class="picker-title">
            Humidifiers
          </div>
          <div v-if="anyTimedOutputConfigured" class="picker-title">
            Timed outputs
          </div>
          <ClockPicker v-if="anyDryingEnabled || anyHeatingUnitClocksEnabled || anyTimedOutputConfigured || anyHumidifiersInTimedOutputMode"
            :bins="bins"
            :options="onOffOptions"
            v-model:selectedOption="selectedOption"
          ></ClockPicker>
        </div>
        <div v-if="!firstRender" class="bins-scroll">
          <BinClocks v-for="(bin, n) in bins"
            class="bin-clocks"  
            :key="n"
            :bin="bin" 
            :committed="committed"
            :modeOptions="modeOptions"
            :purgeOptions="purgeOptions"
            :onOffOptions="onOffOptions"
            :selectedOption="selectedOption"
            :fillSize="selectedFillSize"
            :onChange="onChange"
          ></BinClocks>
        </div>
      </div>
      <div class="submit-buttons" :style="{visibility: showSubmit ? 'visible' : 'hidden'}">
        <Button raised severity="primary" @click="submit()">
          Submit
        </Button>
        <Button raised severity="danger" @click="cancel()">
          Cancel
        </Button>
      </div>
    </WhenOrientation>
  </div>
</template>

<script>
import { readonly } from "vue";
import { groupBy } from "./utility.js";
import { isAssigned } from "./appUtility.js";
import store from "./store.js";
import WhenOrientation from "./WhenOrientation.vue";
import ClockPicker from "./ClockPicker.vue";
import BinClocks from "./BinClocks.vue";
import Checkbox from "./Checkbox.vue";

function cloneBins() {
  let sessions = store.sessions.filter(session => session.system);
  let systems = sessions.map(session => session.system.$clone());
  let systemsSorted = systems.sort((s1, s2) => {
    let boardsOnSite = store.sessions[0].system.boardsOnSite.children;
    let getSortIndex = (system) => boardsOnSite.findIndex(board =>
      board.macAddress.value == system.macAddr.value ||
      board.boardName.value == system.boardName.value
    );
    return getSortIndex(s1) - getSortIndex(s2);
  });
  let bins = systemsSorted.flatMap(system => system.bins.children);
  return bins;
}

let modeOptions = readonly([
  { value: 0, label: "Off", color: "var(--mode-off)" },
  { value: 1, label: "Constant", color: "var(--mode-constant)" },
  { value: 2, label: "Auto", color: "var(--mode-auto)" },
  { value: 3, label: "Air avail", color: "var(--mode-air-avail)" },
  { value: 4, label: "Recirculate", color: "var(--mode-recirculate)" }
]);

let purgeOptions = readonly([
  { 
    value: 0, 
    label: "Off",
    color: "var(--purge-none)"
  },
  { 
    value: 1, 
    label: "Sensor purge",
    color: "var(--purge-sensor)", 
    pickerCondition: bins => bins.some(bin => isAssigned(bin.co2Level)),
    binCondition: bin => isAssigned(bin.co2Level),
    striped: true
  },
  { 
    value: 2, 
    label: "Force purge",
    color: "var(--purge-force)"
  }
]);

let onOffOptions = readonly([
  { value: 0, label: "Off", color: "var(--timedOutput-off)" },
  { value: 1, label: "On", color: "var(--timedOutput-on)" }
]);

export default {
  components: { ClockPicker, BinClocks, Checkbox, WhenOrientation },
  data() {
    return {
      store,
      showSubmit: false,
      committed: true,
      firstRender: true,
      selectedFillSize: 60,
      selectedOption: modeOptions[0],
      bins: null // see watcher
    }
  },
  created() {
    this.modeOptions = modeOptions;
    this.purgeOptions = purgeOptions;
    this.onOffOptions = onOffOptions;
    this.fillSizeOptions = [
      { value: 15, text: "15m" },
      { value: 30, text: "30m" },
      { value: 60, text: "1h" },
      { value: 120, text: "2h" },
    ];
    setTimeout(() => this.firstRender = false);

    this.$router.beforeEach(() => {
      if (this.showSubmit) {
        alert("Submit or cancel changes");
        return false;
      }
    });
  },
  computed: {
    anyPurgeEnabled() {
      return this.bins.some(bin => bin.co2Enabled.value);
    },
    anyDryingEnabled() {
      return this.bins.some(bin => bin.dryingEnabled.value);
    },
    anyHeatingUnitClocksEnabled() {
      return this.bins.some(bin => bin.heatingUnits.children.some(heatingUnit => heatingUnit.clockEnabled.value));
    },
    anyHumidifiersInTimedOutputMode() {
      let humidifiers = this.bins.flatMap(bin => bin.humidifiers.children);
      return humidifiers.some(humidifier => humidifier.userMode.value == "byTimeClock");
    },
    anyTimedOutputConfigured() {
      let timedOutputs = this.bins.flatMap(bin => bin.timedOutputs.children);
      return timedOutputs.some(timedOutput => isAssigned(timedOutput));
    },
    onOffLabel() {
      if (this.anyDryingEnabled && this.anyTimedOutputConfigured) 
        return "Drying / Timed output";
      else if (this.anyDryingEnabled)
        return "Drying";
      else if (this.anyTimedOutputConfigured)
        return "Timed output";
    },
    sessionIds() {
      let sessions = store.sessions.filter(session => session.isConnected);
      return sessions.map(session => session.sessionId);
    }
  },
  watch: {
    sessionIds: {
      immediate: true,
      handler() {
        this.bins = cloneBins();
      }
    }
  },
  methods: {
    filterOptions(bin, options) {
      return options.filter(option =>
        option.condition == null ||
        option.condition(bin)
      );
    },
    fillSizeOptionsClass(option) {
      return {
        "selected": option.value == this.selectedFillSize
      };
    },
    onChange() {
      this.showSubmit = true;
    },
    submit() {
      this.committed = false;
      this.showSubmit = false;
      let binsBySession = groupBy(this.bins, bin => store.sessions.indexOf(bin.$session));
      Object.keys(binsBySession).forEach(sessionIndex => {
        let session = store.sessions[sessionIndex];
        let bins = binsBySession[sessionIndex];
        let settings = bins.flatMap(bin => [
          bin.co2Enabled,
          bin.dryingEnabled,
          bin.reconditioning,
          bin.modeSchedule,
          bin.purgeSchedule,
          bin.dryingSchedule,
          ... bin.heatingUnits.children.flatMap(heatingUnit => [
            heatingUnit.clockEnabled,
            heatingUnit.clock
          ]),
          ... bin.humidifiers.children.flatMap(humidifier => [
            humidifier.timedOutputSchedule
          ]),
          ... bin.timedOutputs.children.flatMap(timedOutput => [
            timedOutput.schedule
          ])
        ]);
        session.changeSettings(settings, () => this.committed = true);
      });
    },
    cancel() {
      this.bins = cloneBins();
      this.showSubmit = false;
    }
  }
}
</script>

<style>
:root { 
  --offColor: #f3f3f3;
  --uncommitted-run: white;
  --mode-off: var(--offColor);
  --mode-constant: #5ce62e;
  --mode-auto: gold;
  --mode-air-avail: #33ccff;
  --mode-recirculate: #f032e6;
  --purge-none: var(--offColor);
  --purge-force: #7a00ff;
  --purge-sensor: #ab5dff;
  --timedOutput-off: var(--offColor);
  --timedOutput-on: #4caf50;
}
</style>

<style scoped>
.clocks-page {
  user-select: none;
}
.clocks-page--toolbar {
  padding: 10px;
  border-right: 1px solid #aaa;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  overflow: auto;
}
.picker-title {
  margin-bottom: -10px;
  font-weight: bold;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}
.bins-scroll {
  flex-grow: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 1in;
}
.bin-clocks {
  padding: 10px;
  padding-top: 20px;
  padding-bottom: 25px;
  background: white;
}
.submit-buttons {
  position: fixed;
  bottom: 15px;
  right: 15px;
  z-index: 5;
  padding: 10px;
  border-radius: 5px;
  display: flex;
  gap: 10px;
}
.submit-buttons button {
  height: 0.5in;
  width: 1.25in;
  font-size: large;
  display: flex;
  justify-content: center;
}
button.fillSize {
  height: 35px;
  flex-grow: 1;
  flex-basis: 0;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: black;
  border: 1px solid gray;
}
button.fillSize.selected {
  color: white;
  background: var(--primary-color);
  border: 1px solid black;
}
button.submit { 
  border-color: green;
}
button.cancel { 
  border-color: red; 
}

@media (min-width: 1200px) {
  .toolbar {
    padding: 10px;
    padding-left: 10px;
  }
  .picker-title {
    font-size: large;
  }
  .clock-picker {
    font-size: large;
  }
  button.fillSize {
    font-size: 15px;
  }
}
</style>
